import { useMemo } from 'react';
import { useCustom } from '@refinedev/core';
import { Clock, Users } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface CountryBreakdownRow {
  country: string;
  uniqueViewers: number;
  totalWatchedSeconds: number;
  percentOfTotalSeconds: number;
}

interface DeviceBreakdownRow {
  device: 'desktop' | 'mobile' | 'tablet' | 'tv' | 'bot' | 'unknown';
  uniqueViewers: number;
  totalWatchedSeconds: number;
  percentOfTotalSeconds: number;
}

interface RetentionPoint {
  minuteFromStart: number;
  activeViewers: number;
  percentOfPeak: number;
}

interface AudienceResponse {
  totalUniqueViewers: number;
  totalWatchedSeconds: number;
  countries: CountryBreakdownRow[];
  devices: DeviceBreakdownRow[];
  retention: RetentionPoint[];
}

const DEVICE_LABELS: Record<DeviceBreakdownRow['device'], string> = {
  desktop: 'Desktop',
  mobile: 'Mobile',
  tablet: 'Tablet',
  tv: 'Smart TV',
  bot: 'Боты',
  unknown: 'Неизвестно',
};

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}с`;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}ч ${minutes}м`;
  }
  return `${minutes}м`;
}

function BarRow({
  label,
  value,
  total,
  rightHint,
}: {
  label: string;
  value: number;
  total: number;
  rightHint: string;
}) {
  const percent = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground tabular-nums">{rightHint}</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
    </div>
  );
}

export function LivestreamAudiencePanel({ livestreamId }: { livestreamId: number }) {
  const { query } = useCustom<AudienceResponse>({
    url: `/admin/livestreams/${livestreamId}/audience`,
    method: 'get',
    queryOptions: {
      refetchInterval: 60_000,
    },
  });

  const data = query.data?.data;

  const retentionData = useMemo(() => {
    if (!data?.retention) {
      return [];
    }
    return data.retention.map((point) => ({
      ...point,
      label: `${point.minuteFromStart} мин`,
    }));
  }, [data]);

  if (query.isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        Загрузка аудитории...
      </div>
    );
  }

  const countries = data?.countries ?? [];
  const devices = data?.devices ?? [];

  if (!data || (countries.length === 0 && devices.length === 0)) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Пока нет данных об аудитории трансляции.
        </CardContent>
      </Card>
    );
  }

  const maxCountrySeconds = Math.max(
    ...countries.map((c) => c.totalWatchedSeconds),
    1,
  );
  const maxDeviceSeconds = Math.max(
    ...devices.map((d) => d.totalWatchedSeconds),
    1,
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <CardContent className="flex items-center gap-4 py-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </div>
            <div className="space-y-0.5">
              <p className="text-xs text-muted-foreground">Уникальные зрители</p>
              <p className="text-2xl font-semibold tabular-nums">
                {data.totalUniqueViewers.toLocaleString('ru-RU')}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 py-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Clock className="h-5 w-5" />
            </div>
            <div className="space-y-0.5">
              <p className="text-xs text-muted-foreground">Суммарное время просмотра</p>
              <p className="text-2xl font-semibold tabular-nums">
                {formatDuration(data.totalWatchedSeconds)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
        <CardHeader>
          <CardTitle className="text-base">География</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {countries.length === 0 ? (
            <p className="text-sm text-muted-foreground">Нет данных по странам.</p>
          ) : (
            countries.slice(0, 10).map((row) => (
              <BarRow
                key={row.country}
                label={row.country}
                value={row.totalWatchedSeconds}
                total={maxCountrySeconds}
                rightHint={`${row.uniqueViewers} чел · ${formatDuration(row.totalWatchedSeconds)} · ${row.percentOfTotalSeconds.toFixed(1)}%`}
              />
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Устройства</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {devices.length === 0 ? (
            <p className="text-sm text-muted-foreground">Нет данных по устройствам.</p>
          ) : (
            devices.map((row) => (
              <BarRow
                key={row.device}
                label={DEVICE_LABELS[row.device]}
                value={row.totalWatchedSeconds}
                total={maxDeviceSeconds}
                rightHint={`${row.uniqueViewers} чел · ${formatDuration(row.totalWatchedSeconds)} · ${row.percentOfTotalSeconds.toFixed(1)}%`}
              />
            ))
          )}
        </CardContent>
      </Card>

      {retentionData.length > 0 && (
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">
              Retention curve — сколько зрителей оставалось во времени
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={retentionData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis
                    dataKey="minuteFromStart"
                    tickFormatter={(v: number) => `${v}м`}
                    fontSize={11}
                  />
                  <YAxis
                    fontSize={11}
                    tickFormatter={(v: number) => String(v)}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      const valueStr = String(value);
                      if (name === 'activeViewers') {
                        return [`${valueStr} чел`, 'Активных зрителей'];
                      }
                      return [`${valueStr}%`, '% от пика'];
                    }}
                    labelFormatter={(label) => `Минута ${String(label)}`}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="activeViewers"
                    stroke="hsl(var(--primary))"
                    fill="hsl(var(--primary))"
                    fillOpacity={0.25}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
}
